import { Loader2 } from "lucide-react"
import { createContext, type ReactNode, useCallback, useContext, useState } from "react"
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "./alert-dialog"

/** Configuration for a single confirmation dialog invocation. */
interface ConfirmDialogOptions {
	title: string
	message: string
	confirmText?: string
	cancelText?: string
	onConfirm: () => void | Promise<void>
}

/** Context value exposing the imperative confirm() trigger. */
interface ConfirmContextType {
	confirm: (options: ConfirmDialogOptions) => void
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined)

/** Accesses the imperative confirm() dialog trigger; must be used within a ConfirmProvider. */
export function useConfirm() {
	const context = useContext(ConfirmContext)
	if (!context) {
		throw new Error("useConfirm must be used within ConfirmProvider")
	}
	return context
}

/** Provides an imperative confirmation-dialog API (via useConfirm) and renders the shared AlertDialog. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
	// Содержимое живёт отдельно от `open`: при закрытии оно остаётся до конца анимации
	// ухода, иначе текст пропадает из ещё видимого окна.
	const [dialog, setDialog] = useState<ConfirmDialogOptions | null>(null)
	const [open, setOpen] = useState(false)
	const [isLoading, setIsLoading] = useState(false)

	const confirm = useCallback((options: ConfirmDialogOptions) => {
		setDialog(options)
		setIsLoading(false)
		setOpen(true)
	}, [])

	const handleConfirm = async () => {
		if (!dialog) return

		setIsLoading(true)
		try {
			await dialog.onConfirm()
			// Загрузка не сбрасывается: пока окно уходит, кнопка не должна вернуться к подписи.
			setOpen(false)
		} catch (error) {
			console.error("Confirm action failed:", error)
			setIsLoading(false)
		}
	}

	const handleCancel = () => {
		setOpen(false)
	}

	return (
		<ConfirmContext.Provider value={{ confirm }}>
			{children}
			<AlertDialog open={open} onOpenChange={(next) => !next && setOpen(false)}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{dialog?.title}</AlertDialogTitle>
						<AlertDialogDescription>{dialog?.message}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={handleCancel} disabled={isLoading}>
							{dialog?.cancelText || "Отмена"}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleConfirm}
							disabled={isLoading}
							aria-busy={isLoading || undefined}
							className="bg-destructive hover:bg-destructive/90"
						>
							{isLoading ? <Loader2 className="size-4 animate-spin" /> : null}
							{isLoading ? "Загрузка..." : dialog?.confirmText || "Подтвердить"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</ConfirmContext.Provider>
	)
}
